/**
 * The product type scale.
 *
 * Two voices, one hierarchy:
 *
 *   - `font-display` (Fraunces) carries the *editorial* register — page
 *     titles, section headings, and word headwords. This is what makes the
 *     app read as a study desk rather than a SaaS dashboard.
 *   - `font-sans` (system stack) carries everything functional — body copy,
 *     labels, buttons, chips, metadata.
 *
 * The split is deliberate: serif is reserved for things a learner *reads as
 * subject matter*. If a string is chrome, it is sans. That single rule is
 * what keeps a mixed-font UI from looking arbitrary.
 *
 * Use these instead of hand-rolled `text-*` + `font-*` combinations so the
 * hierarchy stays consistent as pages are redesigned.
 */

/* ── Display ────────────────────────────────────────────────
   The page's single most important string. Fraunces, tight
   tracking, optically balanced. One per screen — everything
   below it steps down from here. */
export const typeDisplayClass =
  "font-display text-3xl sm:text-4xl font-semibold tracking-tight leading-[1.1] text-balance text-stone-900 dark:text-stone-50";

/** Section heading. Sans, because it is orientation, not subject matter. */
export const typeTitleClass =
  "font-sans text-lg sm:text-xl font-semibold tracking-tight leading-snug text-stone-900 dark:text-stone-50";

/** Card / item title. */
export const typeSubtitleClass =
  "font-sans text-base font-semibold leading-snug text-stone-900 dark:text-stone-50";

/** Body copy and descriptions. */
export const typeBodyClass =
  "font-sans text-sm leading-relaxed text-stone-600 dark:text-stone-400";

/**
 * Long-form reading (definitions, guides, policy text).
 *
 * `text-pretty` keeps a single word from being stranded on the last line — the
 * policy and terms pages are nothing but paragraphs, and a lone "questions"
 * under a block reads as a layout bug rather than a sentence ending.
 */
export const typeProseClass =
  "font-sans text-sm sm:text-[0.9375rem] leading-7 text-stone-700 text-pretty dark:text-stone-300";

/* ── Meta ───────────────────────────────────────────────────
   Labels, counts, captions. Always uppercase, wide tracking,
   and small — this is furniture, not content. */
export const typeMetaClass =
  "font-sans text-[11px] font-semibold uppercase tracking-wider text-stone-500 dark:text-stone-400";

/** Low-emphasis inline text: timestamps, secondary counts. */
export const typeCaptionClass =
  "font-sans text-xs leading-relaxed text-stone-500 dark:text-stone-400";

/* ── Numeric ────────────────────────────────────────────────
   Stats, counts, timers, streaks. Tabular figures so digits
   don't shift width as they change. */
export const typeNumericClass =
  "font-sans tabular-nums tracking-tight text-stone-900 dark:text-stone-50";

/**
 * A word being learned, rendered as a headword. The clearest
 * "this is subject matter" signal in the system — used on
 * vocabulary cards and in the collection creator.
 */
export const typeHeadwordClass =
  "font-display text-xl sm:text-2xl font-semibold tracking-tight leading-tight text-stone-900 dark:text-stone-50";
